import React, { useEffect, useMemo, useState } from "react";
import { Center, Spinner } from "@chakra-ui/react";
import { getMetricById } from "services/MetricService";
import { Header, MetricProto } from "build-ts/metadata";
import showToast from "hooks/useCustomToast";
import KnowledgeGraphVisualizer from "../components/KnowledgeGraphVisualizer";

/**
 * Loads one metric (with edges) and renders it with the same graph UI as the full MKG page
 * (icon nodes, labels below, force-directed layout).
 */
const MKGSubGraphVisualizer: React.FC<{ metricId?: Header }> = ({ metricId }) => {
  const [useMetricKnowledge, setMetricKnowledge] = useState<MetricProto>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchMKG = async () => {
      if (metricId?.id === undefined) {
        setMetricKnowledge(undefined);
        return;
      }
      setLoading(true);
      try {
        const data = await getMetricById(metricId.id, true);
        if (data !== null) {
          setMetricKnowledge(data);
        } else {
          setMetricKnowledge(undefined);
        }
      } catch {
        showToast("Error while Fetching the details", "", "error");
        setMetricKnowledge(undefined);
      } finally {
        setLoading(false);
      }
    };
    fetchMKG();
  }, [metricId?.id]);

  const metricInfoMap = useMemo(() => {
    const m = new Map<string, MetricProto>();
    const id = useMetricKnowledge?.header?.id;
    if (id && useMetricKnowledge) {
      m.set(id, useMetricKnowledge);
    }
    return m;
  }, [useMetricKnowledge]);

  if (!metricId?.id) {
    return null;
  }

  if (loading || !useMetricKnowledge || metricInfoMap.size === 0) {
    return (
      <Center h="100%" minH="200px" bg="gray.50">
        <Spinner size="lg" color="purple.500" thickness="3px" />
      </Center>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        minHeight: "clamp(280px, 62vh, 900px)",
      }}
    >
      <KnowledgeGraphVisualizer metricInfoMap={metricInfoMap} variant="subgraph" />
    </div>
  );
};

export default MKGSubGraphVisualizer;
